import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Select, Form } from 'antd';
import styled from '@emotion/styled';
import produce from 'immer';
import { useMount } from 'ahooks';
import TypeDayPickerImpl from './TypeDayPickerImpl';
import TypeWeekPickerImpl from './TypeWeekPickerImpl';
import TypeYearPickerImpl from './TypeYearPickerImpl';
import { useTranslate } from '@tolgee/react';
import { zIndex } from 'services/config/styles';

type RepeatType = 'YEAR' | 'WEEK' | 'DAY';

function defaultValue(): MultiTimeSelect.EffectiveTime {
  return {
    repeatType: 'DAY',
    dtRange: {
      startAt: undefined,
      endAt: undefined,
    },
  };
}

type MultipleTimePickerProps = {
  // initial value
  value?: MultiTimeSelect.EffectiveTime;
  onChange?: (val: MultiTimeSelect.EffectiveTime) => void;
};

function MultipleTimePicker(props: MultipleTimePickerProps) {
  const { value, onChange } = props;
  const onChangeRef = useRef(onChange);
  const { t } = useTranslate();
  const [type, setType] = useState<RepeatType>(value?.repeatType || 'DAY');
  const [mounted, setMounted] = useState(false);
  const options = useMemo(() => {
    return [
      {
        label: t('multiTimeSelect.每天'),
        value: 'DAY',
      },
      {
        label: t('multiTimeSelect.每周'),
        value: 'WEEK',
      },
      {
        label: t('multiTimeSelect.每年'),
        value: 'YEAR',
      },
    ];
  }, [t]);
  useMount(() => {
    setMounted(true);
  });
  const handleSelectChange = (value: RepeatType) => {
    setInnerValue(
      produce((draft) => {
        draft.repeatType = value;
      }),
    );
    setType(value);
  };
  const [innerValue, setInnerValue] = useState(value || defaultValue());
  const yearTypeInitialValue = useMemo(() => {
    const { yearRepeat } = innerValue;
    return {
      yearRepeat,
    };
  }, [innerValue]);
  const handleYearTypeChange = useCallback(
    (value) => {
      const { yearRepeat } = value;
      setInnerValue(
        produce((draft) => {
          if (!value || !yearRepeat) {
            draft.yearRepeat = [];
          } else {
            draft.yearRepeat = yearRepeat;
          }
        }),
      );
    },
    [setInnerValue],
  );

  const weekTypeInitialValue = useMemo(() => {
    const { weekRepeat, dtRange } = innerValue;
    return {
      weekRepeat,
      dtRange,
    };
  }, [innerValue]);

  const handleWeekTypeChange = useCallback(
    (value) => {
      setInnerValue(
        produce((draft) => {
          const { dtRange, weekRepeat } = value;
          draft.dtRange = dtRange;
          draft.weekRepeat = weekRepeat;
        }),
      );
    },
    [setInnerValue],
  );

  const dayTypeInitialValue = useMemo(() => {
    const { dayRepeat, dtRange } = innerValue;
    return {
      dayRepeat,
      dtRange,
    };
  }, [innerValue]);
  const handleDayTypeChange = useCallback(
    (value) => {
      setInnerValue(
        produce((draft) => {
          const { dtRange, dayRepeat } = value;
          draft.dtRange = dtRange;
          draft.dayRepeat = dayRepeat;
        }),
      );
    },
    [setInnerValue],
  );

  useEffect(() => {
    onChangeRef.current?.(innerValue);
  }, [innerValue, onChangeRef]);

  const ref = useRef(null);
  const select = useMemo(() => {
    if (type === 'YEAR') {
      return (
        <TypeYearPickerImpl
          hookRef={ref}
          value={yearTypeInitialValue}
          onChange={handleYearTypeChange}
        />
      );
    }
    if (type === 'WEEK') {
      return (
        <TypeWeekPickerImpl
          hookRef={ref}
          value={weekTypeInitialValue}
          onChange={handleWeekTypeChange}
        />
      );
    }
    if (type === 'DAY') {
      return (
        <TypeDayPickerImpl
          hookRef={ref}
          value={dayTypeInitialValue}
          onChange={handleDayTypeChange}
        />
      );
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    type,
    handleYearTypeChange,
    yearTypeInitialValue,
    handleDayTypeChange,
    dayTypeInitialValue,
    weekTypeInitialValue,
    ref.current,
    mounted,
  ]);

  const expandWidth = useMemo(() => {
    if (type === 'DAY') {
      return '350px';
    }
    if (type === 'WEEK') {
      return '480px';
    }
    return '480px';
  }, [type]);
  return (
    <Wrapper>
      <Origin>
        <TypeSelect>
          <Form.Item name="type" noStyle>
            <Select defaultValue={type} onChange={handleSelectChange}>
              {options.map((option) => {
                return (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        </TypeSelect>
        <Form.Item
          name="i-dont-care"
          shouldUpdate
          dependencies={['type']}
          style={{ zIndex: zIndex.lower, width: '418px' }}
        >
          <SelectArea>{select}</SelectArea>
        </Form.Item>
      </Origin>
      <ExpandWrap>
        <Expand style={{ width: expandWidth }} ref={ref} />
      </ExpandWrap>
    </Wrapper>
  );
}

const Wrapper = styled.div``;

const TypeSelect = styled.div`
  min-width: 76px;
  z-index: 1;
  &:hover,
  &:active,
  &:focus {
    z-index: 4;
  }
`;

const SelectArea = styled.div`
  margin-left: -1px;
  z-index: 2;
  &:hover {
    z-index: 3;
  }
`;

const Origin = styled.div`
  display: flex;
  & > .ant-form-item {
    margin-bottom: 0;
  }
`;

const ExpandWrap = styled.div`
  background: var(--fett-erp-card-bg);
  &:not(:empty) {
    padding: 16px;
    margin-top: 8px;
  }
`;

const Expand = styled.div`
  .ant-form-item {
    margin-bottom: 0;
  }
`;

export default MultipleTimePicker;
